import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Photo, PrintSize } from '../types';

interface CollectionsPageProps {
  onCategoryClick: (category: string) => void;
  onViewDetails?: (photo: Photo) => void;
  onAddToCart?: (photo: Photo, size: PrintSize) => void;
  photos?: Photo[];
  printSizes?: PrintSize[];
}

const collections = [
  { id: 'street', title: 'Street & Graffiti', edition: 10 },
  { id: 'portrait', title: 'People', edition: 10 },
  { id: 'nature', title: 'Nature', edition: 10 },
  { id: 'blackwhite', title: 'Black & White', edition: 10 },
];

/* ── seeded random: pick 3 photos per category, rotating every 5 days ── */
function seededShuffle(arr: Photo[], seed: number): Photo[] {
  const copy = [...arr];
  let s = seed;
  for (let i = copy.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647; // Park-Miller LCG
    const j = s % (i + 1);
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  return copy;
}

function getRotationSeed(categoryId: string): number {
  // Changes every 5 days based on epoch
  const fiveDayPeriod = Math.floor(Date.now() / (5 * 24 * 60 * 60 * 1000));
  // Simple hash from category string
  let hash = 0;
  for (let i = 0; i < categoryId.length; i++) {
    hash = ((hash << 5) - hash + categoryId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash + fiveDayPeriod);
}

function pickRandomPhotos(photos: Photo[], categoryId: string, count: number): Photo[] {
  if (photos.length <= count) return photos;
  const seed = getRotationSeed(categoryId);
  return seededShuffle(photos, seed).slice(0, count);
}

/* ── single print card ─────────────────────────────────── */
function PrintCard({
  photo,
  index,
  edition,
  categoryLabel,
  sizes,
  onViewDetails,
  onAddToCart,
}: {
  photo: Photo;
  index: number;
  edition: number;
  categoryLabel: string;
  sizes: PrintSize[];
  onViewDetails?: (photo: Photo) => void;
  onAddToCart?: (photo: Photo, size: PrintSize) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="w-full border border-white/[0.04] overflow-hidden"
    >
      {/* Image */}
      <button
        onClick={() => onViewDetails?.(photo)}
        className="relative w-full aspect-[4/3] overflow-hidden group focus:outline-none block"
      >
        <img
          src={photo.imageUrl}
          alt={photo.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {/* Availability badge */}
        {photo.sizes && photo.sizes.length > 0 && (() => {
          const minAvailable = Math.min(...photo.sizes.map(s => s.available ?? 10));
          const isSoldOut = minAvailable <= 0;
          return (
            <div className={`absolute top-3 right-3 px-4 py-2 text-[11px] tracking-[2.5px] uppercase font-light border ${
              isSoldOut 
                ? 'border-red-700/60 text-red-400/80' 
                : 'border-white/25 text-white/60'
            }`}>
              {isSoldOut ? 'Sold Out' : `${minAvailable} Remaining`}
            </div>
          );
        })()}
      </button>

      {/* Info */}
      <div className="px-5 pt-5 pb-1">
        <p className="text-yellow-400 text-[9px] tracking-[2px] uppercase font-light">
          {categoryLabel} • Limited Edition
        </p>
        <p
          className="text-white text-base mt-1 tracking-[0.3px]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {photo.title}
        </p>
      </div>

      {/* Size / price row */}
      <div className="flex border-t border-white/[0.06] mt-3">
        {sizes.map((size) => (
          <button
            key={size.id}
            onClick={() => onAddToCart?.(photo, size)}
            className="flex-1 py-3 flex flex-col items-center gap-0.5 border-r border-white/[0.06] last:border-r-0 hover:bg-white/[0.03] transition-colors focus:outline-none"
          >
            <span className="text-[10px] text-white/35 font-light">{size.name}</span>
            <span className="text-[12px] text-white">
              ${size.price.toLocaleString()}
            </span>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

/* ── category section ─────────────────────────────────── */
function CategorySection({
  collection,
  photos,
  sizes,
  onViewDetails,
  onAddToCart,
  onSeeAll,
  sectionIndex,
}: {
  collection: typeof collections[0];
  photos: Photo[];
  sizes: PrintSize[];
  onViewDetails?: (photo: Photo) => void;
  onAddToCart?: (photo: Photo, size: PrintSize) => void;
  onSeeAll: () => void;
  sectionIndex: number;
}) {
  const headerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(headerRef, { once: true, margin: '-40px 0px' });

  if (photos.length === 0) return null;

  return (
    <div className="mb-16 lg:mb-24">
      {/* Section header */}
      <motion.div
        ref={headerRef}
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
        className="flex items-end justify-between mb-5 lg:mb-6 px-1"
      >
        <div>
          <p className="text-yellow-400/60 text-[10px] tracking-[3px] uppercase font-light mb-1">
            {collection.id === 'blackwhite' ? 'Monochrome' : collection.title.split(' ')[0]}
          </p>
          <h2
            className="text-white text-2xl lg:text-3xl tracking-[0.5px]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {collection.title}
          </h2>
        </div>
        <button
          onClick={onSeeAll}
          className="text-white/30 text-[11px] tracking-[2px] uppercase hover:text-white/60 transition-colors focus:outline-none pb-1"
        >
          See all →
        </button>
      </motion.div>

      {/* Print cards */}
      <div className="grid grid-cols-1 gap-4 lg:gap-5">
        {photos.slice(0, 3).map((photo, index) => (
          <PrintCard
            key={photo.id}
            photo={photo}
            index={sectionIndex * 3 + index}
            edition={collection.edition}
            categoryLabel={collection.title}
            sizes={sizes}
            onViewDetails={onViewDetails}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>
    </div>
  );
}

/* ── main component ───────────────────────────────────── */
export function CollectionsPage({
  onCategoryClick,
  onViewDetails,
  onAddToCart,
  photos = [],
  printSizes: sizes = [],
}: CollectionsPageProps) {
  return (
    <div className="min-h-screen bg-black pt-0 lg:pt-[60px] pb-24">
      {/* ── Hero header ── */}
      <motion.div
        className="text-center pt-20 lg:pt-8 mb-4 lg:mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <p className="text-yellow-400 text-[11px] tracking-[4px] uppercase font-light mb-3">
          Print of the Week
        </p>
        <h1
          className="text-white font-light tracking-[0.5px] mb-4 text-[#e0d8d8] text-[32px]"
          style={{ fontFamily: "'Urbanist', sans-serif" }}
        >
          Fine Art Prints
        </h1>
        <p className="text-white/45 text-sm font-light max-w-md mx-auto leading-relaxed">
          Museum-quality giclée prints on Hahnemühle Photo Rag 310gsm. Shipped worldwide.
        </p>
      </motion.div>

      {/* ── Category sections ── */}
      <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
        {collections.map((col, sectionIndex) => {
          const categoryPhotos = photos.filter((p) => p.category === col.id);
          const randomPhotos = pickRandomPhotos(categoryPhotos, col.id, 3);
          return (
            <CategorySection
              key={col.id}
              collection={col}
              photos={randomPhotos}
              sizes={sizes}
              onViewDetails={onViewDetails}
              onAddToCart={onAddToCart}
              onSeeAll={() => onCategoryClick(col.id)}
              sectionIndex={sectionIndex}
            />
          );
        })}

        {/* If no photos at all */}
        {photos.length === 0 && (
          <motion.div
            className="text-center py-24"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-white/30 text-sm font-light tracking-wide">
              Prints coming soon...
            </p>
          </motion.div>
        )}
      </div>

      {/* ── Footer dedication ── */}
      <motion.div
        className="text-center mt-20 lg:mt-32"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 1 }}
      >
        <p className="text-white/20 text-[13px] italic font-light tracking-[0.35px]">
          In memory of my friend Þorleifur
        </p>
      </motion.div>
    </div>
  );
}

export default CollectionsPage;